import { AsYouType, CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';

const DEFAULT_PHONE_COUNTRY: CountryCode = 'US';

function stripToPhoneChars(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  return hasPlus ? `+${digits}` : digits;
}

/** Format phone input as the user types (e.g. US: (555) 123-4567). */
export function formatPhoneInput(
  value: string,
  defaultCountry: CountryCode = DEFAULT_PHONE_COUNTRY
): string {
  const raw = stripToPhoneChars(value);
  if (!raw) {
    return '';
  }

  return new AsYouType(defaultCountry).input(raw);
}

/** Normalize masked/formatted input to a dialable value (E.164 when valid). */
export function normalizePhoneForDial(
  value: string,
  defaultCountry: CountryCode = DEFAULT_PHONE_COUNTRY
): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  const parsed =
    parsePhoneNumberFromString(trimmed) ?? parsePhoneNumberFromString(trimmed, defaultCountry);

  if (parsed?.isValid()) {
    return parsed.format('E.164');
  }

  return stripToPhoneChars(trimmed);
}

/**
 * Format a phone number for UI display using libphonenumber-js.
 * Returns the original trimmed value when parsing fails (e.g. caller names, IDs).
 */
export function formatPhoneForDisplay(
  value: string | null | undefined,
  defaultCountry: CountryCode = DEFAULT_PHONE_COUNTRY
): string {
  if (value == null) {
    return '';
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  const parsed =
    parsePhoneNumberFromString(trimmed) ?? parsePhoneNumberFromString(trimmed, defaultCountry);

  if (parsed?.isValid()) {
    return parsed.formatInternational();
  }

  return trimmed;
}

export function formatPhoneIfPresent(value: string | null | undefined): string | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  return formatPhoneForDisplay(value);
}
